// Auth — Hosted-UI OAuth (Authorization Code) with direct token management.
//
// Cleaned up 2026-06-24: dropped `amazon-cognito-identity-js`. Login, the code
// exchange, AND refresh all go through the SAME Cognito Hosted-UI endpoint
// (`${COGNITO_DOMAIN}/oauth2/{authorize,token}`) — one consistent, CORS-friendly
// path, no separate SDK refresh against cognito-idp. Tokens live in localStorage
// under the existing `CognitoIdentityServiceProvider.<clientId>.*` keys (so
// already-signed-in users are NOT logged out by this change).
//
// Error model — callers distinguish two failure kinds:
//   - ReauthRequired  → no/expired session that can't be refreshed → sign in again
//                       (seamless via SSO; not a dead-end error screen).
//   - plain Error     → a transient/network/server hiccup → retryable.

const API_URL = import.meta.env.VITE_API_URL;
const WEB_CLIENT_ID = import.meta.env.VITE_CRM_WEB_CLIENT_ID ?? import.meta.env.VITE_WEB_CLIENT_ID;
const COGNITO_DOMAIN = import.meta.env.VITE_CRM_COGNITO_DOMAIN ?? import.meta.env.VITE_COGNITO_DOMAIN;
const COGNITO_SCOPE = import.meta.env.VITE_CRM_COGNITO_SCOPE ?? "openid";

const missingConfigMessage =
  "Missing required env vars: VITE_API_URL, VITE_CRM_WEB_CLIENT_ID, VITE_CRM_COGNITO_DOMAIN. " +
  "Create a web/.env file with these values.";

/** Thrown when there is no usable session and it can't be refreshed — the caller
 *  should send the user (back) through sign-in rather than show an error. */
export class ReauthRequired extends Error {
  constructor(message = "Sign-in required") {
    super(message);
    this.name = "ReauthRequired";
  }
}

// --- token store (existing CognitoIdentityServiceProvider.* key layout) ------

function keyPrefix(): string {
  return `CognitoIdentityServiceProvider.${WEB_CLIENT_ID}`;
}
function storedUsername(): string | null {
  if (!WEB_CLIENT_ID) return null;
  return localStorage.getItem(`${keyPrefix()}.LastAuthUser`);
}
function readToken(kind: "idToken" | "accessToken" | "refreshToken"): string | null {
  const u = storedUsername();
  return u ? localStorage.getItem(`${keyPrefix()}.${u}.${kind}`) : null;
}
function decodeBase64Url(str: string): string {
  const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  return atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4));
}
function jwtPayload(token: string): Record<string, unknown> {
  try {
    return JSON.parse(decodeBase64Url(token.split(".")[1] ?? "")) as Record<string, unknown>;
  } catch {
    return {};
  }
}
/** Seconds until expiry (negative if past). 60s skew so we refresh just before. */
function secondsUntilExpiry(token: string): number {
  const exp = Number(jwtPayload(token).exp ?? 0);
  if (!exp) return -1;
  return exp - Math.floor(Date.now() / 1000) - 60;
}

function storeTokens(t: { id_token: string; access_token: string; refresh_token?: string }): void {
  const payload = jwtPayload(t.id_token);
  const username = String(payload["cognito:username"] ?? payload.sub ?? "");
  const p = keyPrefix();
  localStorage.setItem(`${p}.LastAuthUser`, username);
  localStorage.setItem(`${p}.${username}.idToken`, t.id_token);
  localStorage.setItem(`${p}.${username}.accessToken`, t.access_token);
  if (t.refresh_token) localStorage.setItem(`${p}.${username}.refreshToken`, t.refresh_token);
}
function clearTokens(): void {
  const u = storedUsername();
  const p = keyPrefix();
  if (u) {
    for (const k of ["idToken", "accessToken", "refreshToken"]) localStorage.removeItem(`${p}.${u}.${k}`);
  }
  localStorage.removeItem(`${p}.LastAuthUser`);
}

// --- public surface ----------------------------------------------------------

export function getApiUrl(): string {
  if (!API_URL) throw new Error(missingConfigMessage);
  return API_URL;
}

/** Compat shim for callers that only need "is someone signed in". */
export function getCurrentUser(): { username: string } | null {
  const username = storedUsername();
  return username ? { username } : null;
}

/** Single in-flight refresh so concurrent callers share one network call. */
let refreshInFlight: Promise<string> | null = null;

async function refreshIdToken(): Promise<string> {
  if (!COGNITO_DOMAIN || !WEB_CLIENT_ID) throw new Error(missingConfigMessage);
  const refreshToken = readToken("refreshToken");
  if (!refreshToken) throw new ReauthRequired("No refresh token");

  const res = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: WEB_CLIENT_ID,
      refresh_token: refreshToken,
    }),
  }).catch((e) => {
    // Network failure — transient, NOT a reason to wipe the session.
    throw new Error(e instanceof Error ? e.message : "Network error");
  });

  // 400/401 = invalid_grant (refresh token revoked/expired) → genuine re-auth.
  if (res.status === 400 || res.status === 401) {
    clearTokens();
    throw new ReauthRequired("Session expired");
  }
  if (!res.ok) throw new Error(`Token refresh failed (${res.status})`);

  const t = (await res.json()) as { id_token: string; access_token: string; refresh_token?: string };
  // The refresh grant does not return a new refresh_token — keep the existing one.
  storeTokens({ ...t, refresh_token: t.refresh_token ?? refreshToken });
  return t.id_token;
}

/** Return a valid ID token, refreshing if expired. Throws ReauthRequired when
 *  there is no session that can be made valid. */
export async function getIdToken(): Promise<string> {
  const id = readToken("idToken");
  if (!id) throw new ReauthRequired("Not signed in");
  if (secondsUntilExpiry(id) > 0) return id;
  if (!refreshInFlight) {
    refreshInFlight = refreshIdToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Auth headers for direct fetches (agent widget streaming client). Mirrors
 *  brain-api's adminFetch: a fresh (auto-refreshed) id token as a Bearer. */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

/** Compat: resolve when a usable session exists (refreshing if needed). */
export async function getSession(): Promise<void> {
  await getIdToken();
}

export function signOut(): void {
  clearTokens();
}

// --- Hosted UI sign-in -------------------------------------------------------

const OAUTH_STATE_KEY = "oauth_state";

function getRedirectUri(): string {
  return `${window.location.origin}/callback`;
}
function generateOAuthState(): string {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function signInWithAeronet(): void {
  if (!COGNITO_DOMAIN || !WEB_CLIENT_ID) throw new Error(missingConfigMessage);
  const state = generateOAuthState();
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  const params = new URLSearchParams({
    redirect_uri: getRedirectUri(),
    response_type: "code",
    client_id: WEB_CLIENT_ID,
    scope: COGNITO_SCOPE,
    state,
  });
  window.location.href = `${COGNITO_DOMAIN}/oauth2/authorize?${params}`;
}

export async function handleOAuthCallback(code: string, state: string | null): Promise<void> {
  if (!COGNITO_DOMAIN || !WEB_CLIENT_ID) throw new Error(missingConfigMessage);

  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  if (!expectedState) throw new Error("OAuth state not found — try signing in again");
  if (!state) throw new Error("OAuth state missing from callback URL");
  if (expectedState !== state) throw new Error("OAuth state mismatch — possible CSRF attempt");

  const res = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: WEB_CLIENT_ID,
      redirect_uri: getRedirectUri(),
      code,
    }),
  });
  if (!res.ok) throw new Error(`Token exchange failed (${res.status})`);

  const tokens = (await res.json()) as { id_token: string; access_token: string; refresh_token?: string };
  storeTokens(tokens);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
}
